export const CLOUD_ORIGIN = 'https://www.entermtrx.com';
export const LOCAL_ORIGIN = 'http://localhost:5001';

const getStoredTarget = () => {
  if (typeof window === 'undefined') return 'auto';
  return localStorage.getItem('admin_api_target') || 'auto'; // 'auto' | 'local' | 'cloud'
};

const resolveInitialOrigin = () => {
  if (typeof window === 'undefined') return CLOUD_ORIGIN;
  if (window.location.hostname.includes('www.entermtrx.com')) return window.location.origin;

  const target = getStoredTarget();
  if (target === 'local') return LOCAL_ORIGIN;
  if (target === 'cloud') return CLOUD_ORIGIN;

  // 'auto': check if previously resolved or default to local for localhost development
  const cached = localStorage.getItem('admin_active_origin');
  if (cached) return cached;
  return LOCAL_ORIGIN;
};

export let API_ORIGIN = process.env.REACT_APP_API_ORIGIN || resolveInitialOrigin();
export let BASE_URL = process.env.REACT_APP_API_URL || `${API_ORIGIN}/api`;

export const getApiTarget = () => getStoredTarget();

export const switchApiTarget = (target) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('admin_api_target', target);
    if (target === 'local') {
      API_ORIGIN = LOCAL_ORIGIN;
      localStorage.setItem('admin_active_origin', LOCAL_ORIGIN);
    } else if (target === 'cloud') {
      API_ORIGIN = CLOUD_ORIGIN;
      localStorage.setItem('admin_active_origin', CLOUD_ORIGIN);
    } else {
      localStorage.removeItem('admin_active_origin');
    }
    BASE_URL = `${API_ORIGIN}/api`;
    window.location.reload();
  }
};

export const isUsingLocalApi = () => {
  return API_ORIGIN.includes('localhost') || API_ORIGIN.includes('127.0.0.1');
};

export const resolveAdminMediaUri = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  return `${API_ORIGIN}${url.startsWith('/') ? url : `/${url}`}`;
};

let adminToken = localStorage.getItem('admin_token') || null;

export const setToken = (token) => {
  adminToken = token;
  if (token) {
    localStorage.setItem('admin_token', token);
  } else {
    localStorage.removeItem('admin_token');
  }
};

export const getToken = () => adminToken;

export const isLoggedIn = () => !!adminToken;

export const logout = () => {
  setToken(null);
};

const request = async (endpoint, options = {}, isRetry = false) => {
  let url = `${BASE_URL}${endpoint}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...(adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {}),
    ...options.headers,
  };

  const config = {
    ...options,
    headers,
  };

  try {
    const res = await fetch(url, config);
    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = { message: text };
    }

    if (!res.ok) {
      throw new Error(data.message || data.msg || `Request failed with status ${res.status}`);
    }

    return data;
  } catch (error) {
    // Automatic failover between local and cloud if target is 'auto' and on localhost
    const isNetworkError = error.message.includes('fetch') || error.message.includes('Failed to fetch') || error.message.includes('NetworkError');
    if (!isRetry && isNetworkError && getStoredTarget() === 'auto' && typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      const alternateOrigin = API_ORIGIN === LOCAL_ORIGIN ? CLOUD_ORIGIN : LOCAL_ORIGIN;
      console.warn(`[Admin API] Connection to ${API_ORIGIN} failed. Seamlessly switching to ${alternateOrigin}...`);
      API_ORIGIN = alternateOrigin;
      BASE_URL = `${API_ORIGIN}/api`;
      localStorage.setItem('admin_active_origin', alternateOrigin);
      return request(endpoint, options, true);
    }

    console.error(`API Error in ${endpoint}:`, error.message);
    throw error;
  }
};

export const loginAdmin = async (email, password) => {
  const data = await request('/auth/admin/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
  if (data.success && data.data && data.data.token) {
    setToken(data.data.token);
  }
  return data;
};

export const getDashboardStats = () => request('/admin/dashboard');

export const getAdminSettings = () => request('/admin/settings');

export const uploadCityVideo = async (payload) => {
  if (payload instanceof FormData) {
    const url = `${BASE_URL}/admin/settings/video`;
    const headers = {
      ...(adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {}),
    };
    const res = await fetch(url, { method: 'POST', headers, body: payload });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || data.msg || 'Video upload failed');
    }
    return data;
  }
  return request('/admin/settings/video', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const getCaptains = (status = '') => {
  const query = status ? `?status=${status}` : '';
  return request(`/admin/captains${query}`);
};

export const approveCaptain = (id) => request(`/admin/captains/${id}/approve`, { method: 'PUT' });

export const rejectCaptain = (id, rejectionReason) => request(`/admin/captains/${id}/reject`, {
  method: 'PUT',
  body: JSON.stringify({ rejectionReason })
});

export const getPendingArtists = () => request('/artist/pending');

export const approveArtistProfile = (id) => request(`/artist/${id}/verify`, {
  method: 'PUT',
  body: JSON.stringify({ status: 'approved' })
});

export const rejectArtistProfile = (id, rejectionReason) => request(`/artist/${id}/verify`, {
  method: 'PUT',
  body: JSON.stringify({ status: 'rejected', notes: rejectionReason })
});

export const blockCaptain = (id) => request(`/admin/captains/${id}/block`, { method: 'PUT' });
export const unblockCaptain = (id) => request(`/admin/captains/${id}/unblock`, { method: 'PUT' });

export const unlockCaptainModules = (id, moduleNames, durationDays) => request(`/admin/captains/${id}/unlock-module`, {
  method: 'PUT',
  body: JSON.stringify({ moduleNames, durationDays })
});

export const lockCaptainModule = (id, moduleName) => request(`/admin/captains/${id}/lock-module`, {
  method: 'PUT',
  body: JSON.stringify({ moduleName })
});

export const toggleVendorSubscription = (id, active) => request(`/admin/captains/${id}/toggle-subscription`, {
  method: 'PUT',
  body: JSON.stringify({ active })
});

export const createRoom = (payload) => request('/rooms', {
  method: 'POST',
  body: JSON.stringify(payload)
});

export const getRooms = (status = '') => {
  const query = status ? `?status=${status}` : '';
  return request(`/admin/rooms${query}`);
};

export const approveRoom = (id) => request(`/admin/rooms/${id}/approve`, { method: 'PUT' });

export const rejectRoom = (id, rejectionReason) => request(`/admin/rooms/${id}/reject`, {
  method: 'PUT',
  body: JSON.stringify({ rejectionReason })
});

export const deleteRoom = (id) => request(`/admin/rooms/${id}`, { method: 'DELETE' });

export const toggleRoomFlag = (id, flag, value) => request(`/rooms/${id}/toggle-flag`, {
  method: 'PUT',
  body: JSON.stringify({ flag, value })
});

export const getMarketIntel = () => request('/rooms/market-intel/indore');
export const updateMarketIntel = (data) => request('/rooms/market-intel/indore', {
  method: 'PUT',
  body: JSON.stringify(data)
});

export const getAdminRoommates = () => request('/roommate-matches/admin/all');
export const deleteAdminRoommate = (id) => request(`/roommate-matches/admin/${id}`, { method: 'DELETE' });
export const createRoomDirect = (payload) => request('/rooms', {
  method: 'POST',
  body: JSON.stringify(payload)
});

export const getUsers = () => request('/admin/users');
export const blockUser = (id) => request(`/admin/users/${id}/block`, { method: 'PUT' });
export const unblockUser = (id) => request(`/admin/users/${id}/unblock`, { method: 'PUT' });

export const getBookings = () => request('/admin/bookings');
export const getPayments = () => request('/admin/payments');
export const getReports = () => request('/admin/reports');

export const resolveReport = (id, adminAction) => request(`/admin/reports/${id}/resolve`, {
  method: 'PUT',
  body: JSON.stringify({ adminAction })
});

// Ground booking slots retrieval (public endpoint)
export const getRoomSlots = (roomId, date = '') => {
  const query = date ? `?date=${date}` : '';
  return request(`/rooms/${roomId}/slots${query}`);
};

export const updateBookingStatus = (id, bookingStatus, paymentStatus) => request(`/admin/bookings/${id}`, {
  method: 'PUT',
  body: JSON.stringify({ bookingStatus, paymentStatus })
});

export const createAdminBooking = (payload) => request('/admin/bookings', {
  method: 'POST',
  body: JSON.stringify(payload)
});

export const createTrack = async (payload) => {
  if (payload instanceof FormData) {
    const url = `${BASE_URL}/tracks`;
    const headers = {
      ...(adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {}),
    };
    try {
      console.log('📡 Sending FormData track payload to:', url);
      const res = await fetch(url, { method: 'POST', headers, body: payload });
      const data = await res.json();
      if (!res.ok || !data.success) {
        console.error('❌ Track Upload API Error:', data);
        throw new Error(data.message || data.msg || `Upload failed with HTTP ${res.status}`);
      }
      console.log('✅ Track Upload API Success:', data);
      return data;
    } catch (err) {
      console.error('❌ Track Upload Exception:', err);
      throw err;
    }
  }
  return request('/tracks', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
};

export const getTracks = () => request('/tracks');
export const deleteTrack = (id) => request(`/tracks/${id}`, { method: 'DELETE' });

// --- Advertisements ---
export const getAds = () => request('/ads/admin');
export const createAd = (data) => request('/ads/admin', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateAd = (id, data) => request(`/ads/admin/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteAd = (id) => request(`/ads/admin/${id}`, { method: 'DELETE' });

// --- Razorpay Standard Checkout ---
export const createRazorpayOrder = (payload) => request('/create-order', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const verifyRazorpayPayment = (payload) => request('/verify-payment', {
  method: 'POST',
  body: JSON.stringify(payload),
});

// ─────────────────────────────────────────────────────────────
//  DUMMY PAYMENT GATEWAY (admin & user facing)
// ─────────────────────────────────────────────────────────────

// Create a dummy order
export const createDummyOrder = (payload) => request('/payments/dummy/create-order', {
  method: 'POST',
  body: JSON.stringify(payload),
});

// Instantly capture payment (simulate success)
export const simulateDummyPayment = (payload) => request('/payments/dummy/pay', {
  method: 'POST',
  body: JSON.stringify(payload),
});

// Verify dummy signature
export const verifyDummyPayment = (payload) => request('/payments/dummy/verify', {
  method: 'POST',
  body: JSON.stringify(payload),
});

// Process refund
export const processDummyRefund = (payload) => request('/payments/dummy/refund', {
  method: 'POST',
  body: JSON.stringify(payload),
});

// Admin: Get all dummy payments
export const getAllDummyPayments = (params = '') => request(`/payments/dummy/all${params}`);

// ── Subscription Plans ──────────────────────────────────────
export const getSubscriptionPlans = () => request('/payments/dummy/plans');

export const createSubscriptionPlan = (payload) => request('/payments/dummy/plans', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const updateSubscriptionPlan = (id, payload) => request(`/payments/dummy/plans/${id}`, {
  method: 'PUT',
  body: JSON.stringify(payload),
});

export const deleteSubscriptionPlan = (id) => request(`/payments/dummy/plans/${id}`, { method: 'DELETE' });

// ── Subscriptions ───────────────────────────────────────────
export const getAllSubscriptions = (params = '') => request(`/payments/dummy/subscriptions${params}`);

export const subscribeToPlan = (payload) => request('/payments/dummy/subscribe', {
  method: 'POST',
  body: JSON.stringify(payload),
});

export const cancelSubscription = (id) => request(`/payments/dummy/subscriptions/${id}/cancel`, { method: 'PUT' });

